import React, { useState } from "react";
import {
  CloudIcon,
  SunIcon,
  BoltIcon,
  ArrowsUpDownIcon,
} from "@heroicons/react/24/outline";
import { Cloud, CloudHail, Droplet, Thermometer, Wind } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { WeatherData } from "@/types/weather";
import { motion } from "framer-motion";
import logger from "@/utils/logger";

const getWeatherIcon = (condition: string) => {
  switch (condition.toLowerCase()) {
    case "sunny":
    case "clear":
      return <SunIcon className="w-8 h-8 2xl:w-10 2xl:h-10 text-yellow-500" />;
    case "cloudy":
    case "clouds":
      return <CloudIcon className="w-8 h-8 2xl:w-10 2xl:h-10 text-gray-500" />;
    case "rain":
    case "rainy":
      return <CloudIcon className="w-8 h-8 2xl:w-10 2xl:h-10 text-blue-500" />;
    case "snow":
    case "snowy":
      return <CloudIcon className="w-8 h-8 2xl:w-10 2xl:h-10 text-blue-300" />;
    case "fog":
    case "foggy":
      return <CloudIcon className="w-8 h-8 2xl:w-10 2xl:h-10 text-gray-400" />;
    case "thunder":
    case "thunderstorm":
      return <BoltIcon className="w-8 h-8 2xl:w-10 2xl:h-10 text-yellow-600" />;
    default:
      return <CloudIcon className="w-8 h-8 2xl:w-10 2xl:h-10 text-gray-500" />;
  }
};

const getWeatherBgColor = (condition: string): string => {
  switch (condition.toLowerCase()) {
    case "sunny":
    case "clear":
      return "bg-yellow-50";
    case "cloudy":
    case "clouds":
      return "bg-gray-100";
    case "rain":
    case "rainy":
      return "bg-blue-100";
    case "snow":
    case "snowy":
      return "bg-blue-100/50";
    case "fog":
    case "foggy":
      return "bg-gray-200";
    case "thunder":
    case "thunderstorm":
      return "bg-yellow-200";
    default:
      return "bg-gray-100";
  }
};

interface WeatherViewProps {
  locationId: string;
  name: string;
  weatherData: WeatherData | null;
  isLoading?: boolean;
}

const WeatherView: React.FC<WeatherViewProps> = ({
  locationId,
  name,
  weatherData,
  isLoading = false,
}) => {
  const [isWeatherModalOpen, setIsWeatherModalOpen] = useState(false);

  logger.log(weatherData);

  const handleModalOpen = () => {
    setIsWeatherModalOpen(true);
  };

  const handleModalClose = (open: boolean) => {
    setIsWeatherModalOpen(open);
  };

  if (!locationId) return null;

  return (
    <>
      <button
        onClick={handleModalOpen}
        className="outline-none w-1/2 h-full bg-white rounded-lg p-2 2xl:p-2.5 flex flex-col justify-between items-start group hover:ring-1 hover:ring-indigo-500/50 hover:bg-indigo-50 transition-all duration-300 shadow-sm border border-neutral-100"
        data-tutorial="overview-weather"
      >
        <div className="flex flex-row items-center space-x-1.5">
          <Cloud className="w-4 h-4 text-neutral-800" />
          <h2 className="text-xs 2xl:text-[15px] text-neutral-400 font-light group-hover:text-blue-500 transition-all duration-300">
            Weather
          </h2>
        </div>
        {weatherData ? (
          <h3 className="text-neutral-800 font-medium ~lg/3xl ~text-sm/xl group-hover:text-blue-500 transition-all duration-300">
            {weatherData.temp.toFixed(1)}°C
          </h3>
        ) : (
          <h3 className="text-neutral-800 font-medium text-md 2xl:text-lg group-hover:text-blue-500 transition-all duration-300 mt-auto">
            {isLoading ? (
              <div className="flex items-center space-x-2">
                <motion.div
                  className="h-4 w-12 bg-blue-200 rounded-md"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </div>
            ) : (
              "N/A"
            )}
          </h3>
        )}
      </button>

      <Dialog open={isWeatherModalOpen} onOpenChange={handleModalClose}>
        <DialogContent
          className="sm:max-w-[600px] 2xl:max-w-[700px] p-6"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="text-xl 2xl:text-2xl font-semibold text-neutral-900">
              {name} Weather
            </DialogTitle>
            <DialogDescription className="text-xs 2xl:text-sm text-neutral-500">
              Predicted weather for {name} during your stay
            </DialogDescription>
          </DialogHeader>

          {weatherData ? (
            <div className="flex flex-col gap-6">
              {/* Top Section: Icon and Temperature */}
              <div className="flex items-center gap-4">
                <div
                  className={`p-3 2xl:p-4 rounded-xl ${getWeatherBgColor(
                    weatherData.main
                  )} transition-colors duration-300`}
                >
                  {getWeatherIcon(weatherData.main)}
                </div>
                <div>
                  <p className="text-lg 2xl:text-xl font-medium text-neutral-900">
                    {weatherData.temp.toFixed(1)}°C
                  </p>
                  <p className="text-xs 2xl:text-sm text-neutral-500 capitalize font-light tracking-wide">
                    {weatherData.description}
                  </p>
                </div>
              </div>

              {/* Grid of Weather Info */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  {
                    label: "Min - Max",
                    value: `${weatherData.temp_min.toFixed(
                      1
                    )}°C - ${weatherData.temp_max.toFixed(1)}°C`,
                    Icon: ArrowsUpDownIcon,
                    iconColor: "text-blue-500",
                  },
                  {
                    label: "Feels Like",
                    value: `${weatherData.feels_like.toFixed(1)}°C`,
                    Icon: Thermometer,
                    iconColor: "text-red-500",
                  },
                  {
                    label: "Humidity",
                    value: `${weatherData.humidity}%`,
                    Icon: Droplet,
                    iconColor: "text-sky-500",
                  },
                  {
                    label: "Wind Speed",
                    value: `${weatherData.wind_speed} m/s`,
                    Icon: Wind,
                    iconColor: "text-gray-500",
                  },
                  {
                    label: "Precipitation",
                    value: `${weatherData.precipitation} mm`,
                    Icon: CloudHail,
                    iconColor: "text-blue-600",
                  },
                  {
                    label: "Cloud Cover",
                    value: `${weatherData.clouds}%`,
                    Icon: CloudIcon,
                    iconColor: "text-gray-400",
                  },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-xl bg-white border border-neutral-100 space-y-1.5 flex flex-col"
                  >
                    <div className="flex items-center space-x-1.5 mb-1">
                      {item.Icon === Droplet ||
                      item.Icon === Thermometer ||
                      item.Icon === CloudHail ||
                      item.Icon === Wind ? (
                        <item.Icon
                          className={`w-4 h-4 2xl:w-5 2xl:h-5 ${item.iconColor}`}
                          strokeWidth={1.5}
                        />
                      ) : (
                        <item.Icon
                          className={`w-4 h-4 2xl:w-5 2xl:h-5 ${item.iconColor}`}
                        />
                      )}
                      <p className="text-xs 2xl:text-sm text-neutral-500 font-light">
                        {item.label}
                      </p>
                    </div>
                    <p className="text-sm 2xl:text-base font-medium text-neutral-700 mt-auto">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-4 flex flex-col gap-6">
              {isLoading ? (
                <>
                  {/* Skeleton Loader */}
                  <div className="flex items-center gap-4">
                    <motion.div
                      className="w-16 h-16 rounded-xl bg-neutral-100"
                      animate={{ opacity: [0.5, 0.8, 0.5] }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                    <div className="space-y-2">
                      <motion.div
                        className="h-7 w-28 bg-blue-100 rounded-md"
                        animate={{ opacity: [0.5, 0.8, 0.5] }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      />
                      <motion.div
                        className="h-5 w-36 bg-blue-100 rounded-md"
                        animate={{ opacity: [0.5, 0.8, 0.5] }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: 0.2,
                        }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, index) => (
                      <div
                        key={index}
                        className="p-4 rounded-xl bg-slate-50 shadow-sm h-24"
                      >
                        <motion.div
                          className="h-4 w-20 bg-blue-100 rounded-md mb-3"
                          animate={{ opacity: [0.5, 0.8, 0.5] }}
                          transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: index * 0.1,
                          }}
                        />
                        <motion.div
                          className="h-5 w-16 bg-blue-100 rounded-md"
                          animate={{ opacity: [0.5, 0.8, 0.5] }}
                          transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: 0.2 + index * 0.1,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <CloudIcon className="w-12 h-12 text-neutral-300 mb-3" />
                  <p className="text-neutral-500 text-sm 2xl:text-base">
                    Weather data not available for {name}.
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WeatherView;
